/*
封装所有的ajax请求
形成同一个错误响应,
同时实现统一的缓存效果
*/
import axios from 'axios'
import { Notification } from 'element-ui'
import router from '../router'
import requestUtils from './request-utils'

const {
  DEFAULT_TIMEOUT,
  buildRequestKey,
  isAuthRequired,
  resolveErrorMessage
} = requestUtils

const pendingRequests = new Map()

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '',
  timeout: DEFAULT_TIMEOUT
})

function notifyError (message) {
  Notification({
    type: 'error',
    message
  })
}

function shouldNotify (config) {
  return !(config && config.skipGlobalNotification === true)
}

function addPendingRequest (config) {
  if (!config || config.cancelRepeat === false) {
    return
  }

  const key = buildRequestKey(config)
  const oldController = pendingRequests.get(key)
  if (oldController) {
    oldController.abort()
    pendingRequests.delete(key)
  }

  if (!config.signal) {
    const controller = new AbortController()
    config.signal = controller.signal
    config.__requestKey = key
    config.__pendingController = controller
    pendingRequests.set(key, controller)
  }
}

function removePendingRequest (config) {
  if (!config || !config.__requestKey) {
    return
  }

  const controller = pendingRequests.get(config.__requestKey)
  if (controller === config.__pendingController) {
    pendingRequests.delete(config.__requestKey)
  }
}

function isCanceledRequest (error) {
  return axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
}

function buildErrorResponse (error, message) {
  return {
    data: {
      status: -1,
      msg: message,
      data: null
    },
    status: error?.response?.status || 0,
    statusText: error?.response?.statusText || '',
    headers: error?.response?.headers || {},
    config: error?.config || {},
    request: error?.request,
    __handledError: true
  }
}

function redirectToLogin () {
  window.sessionStorage.removeItem('token')
  router.push('/login')
}

instance.interceptors.response.use(
  (res) => {
    removePendingRequest(res.config)
    if (res.config && res.config.responseType === 'blob') {
      return res
    }
    const status = Number(res.data?.status)
    if (status === -1 || status === 401) {
      if (shouldNotify(res.config)) {
        notifyError(res.data.msg || '请求失败，请稍后重试')
      }
      if (status === 401) {
        redirectToLogin()
      }
      return res
    } else if (status === 0 && res.data.msg !== 'success') {
      if (shouldNotify(res.config)) {
        Notification({
          type: 'success',
          message: res.data.msg
        })
      }
    }
    return res
  },
  error => {
    removePendingRequest(error && error.config)
    if (isCanceledRequest(error)) {
      return new Promise(() => {})
    }

    const responseStatus = error?.response?.status
    if (responseStatus === 401) {
      redirectToLogin()
    }

    if (error?.config?.responseType === 'blob') {
      return Promise.reject(error)
    }

    const message = resolveErrorMessage(error)
    if (shouldNotify(error && error.config)) {
      notifyError(message)
    }
    return buildErrorResponse(error, message)
  }
)

instance.interceptors.request.use(
  req => {
    addPendingRequest(req)
    const { url } = req
    const token = window.sessionStorage.getItem('token')

    if (isAuthRequired(url)) {
      if (!token) {
        removePendingRequest(req)
        notifyError('token不存在, 请重新登录')
        router.push('/login')
        return Promise.reject(new Error('token不存在, 请重新登录'))
      } else {
        req.headers = {
          ...(req.headers || {}),
          Authorization: 'Bearer ' + token
        }
      }
    }
    return req
  },
  error => {
    return Promise.reject(error)
  }
)

/**
 * 暴露出去的接口
 */
export default instance
